/* import __COLOCATED_TEMPLATE__ from './seller-projects-result-item.hbs'; */
import moment from 'moment';
import clsx from 'clsx';
import Component from '@glimmer/component';
import { APPLICATION_STATUS } from 'sh-www/models/application';
import { showModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { htmlSafe } from '@ember/template';

const CLASS_NAME = 'seller-projects-result-item';

const getRequiredLegal = ({ legalAgreements }) => {
  const legalAgreement = legalAgreements.firstObject;

  return legalAgreement && !legalAgreement.accepted
    ? legalAgreement.legal
    : null;
};

class SellerProjectsResultItemComponent extends Component {
  get className() {
    return CLASS_NAME;
  }

  get componentClassNames() {
    return clsx(
      CLASS_NAME,
      this.args.model.isUnread && `${CLASS_NAME}--unread`,
    );
  }

  get statusClassNames() {
    return clsx(
      `${CLASS_NAME}__status`,
      this.args.model.statusColor === 'lsg-red' && `${CLASS_NAME}__status--important`,
    );
  }

  get brandOrCompanyName() {
    const { brand, company } = this.args.model;
    return htmlSafe(brand?.displayedName ?? company.name);
  }

  get lastActivity() {
    return `Last activity ${moment(this.args.model.updatedAt).fromNow()}`;
  }

  get menuItems() {
    const application = this.args.model;
    const menuItems = [];

    const createMenuItem = (title, onClick, hasSeparatorAfter = false) => {
      menuItems.push({ title, onClick, hasSeparatorAfter });
    };

    switch (application.statusEnum) {
      case 'pending':
        createMenuItem('Withdraw', () => this.withdrawApplication(application));
        break;

      case 'pending_direct_no_offer':
        createMenuItem('Decline', () => this.declineApplication(application));
        break;

      case 'publisher_approved':
        createMenuItem('Accept offer', () => this.acceptOffer(application));
        createMenuItem('Counter offer', () => this.counterOffer(application), true);
        createMenuItem('Decline', () => this.declineApplication(application));
        break;

      case 'producer_approved':
        createMenuItem('Decline', () => this.declineApplication(application));
        break;

      case 'publisher_approved_price':
        createMenuItem('Accept offer', () => this.acceptOffer(application), true);
        createMenuItem('Decline', () => this.declineApplication(application));
        break;

      case 'active':
        createMenuItem('Mark complete', () => this.markComplete(application), true);
        createMenuItem('Request advance', () => this.requestAdvance(application));
        break;

      case 'pending_advance_buyer_agreed':
        createMenuItem('Review advance', () => this.reviewAdvance(application), true);
        createMenuItem('Decline advance', () => this.declineAdvance(application));
        break;

      case 'pending_advance_seller_agreed':
        createMenuItem('Cancel advance request', () => this.cancelAdvance(application));
        createMenuItem('Adjust advance request', () => this.reviewAdvance(application));
        break;

      case 'cancel_pending':
        createMenuItem('Confirm cancellation', () => this.confirmCancellation(application));
        break;

      case 'complete_publisher':
        createMenuItem('Confirm completion', () => this.markComplete(application), true);
        break;

      case 'complete':
      case 'paid': {
        const { projectdigest } = application;

        const onClick = () => {
          this.addOrEditLinkToProjectDigest(projectdigest);
        };

        if (!projectdigest?.link) {
          createMenuItem('Add link to published project', onClick);
        } else {
          createMenuItem('Edit link to published project', onClick);
        }

        // removing project digests is also handled in the same modal
        if (projectdigest?.canDelete) {
          createMenuItem('Remove link', onClick);
        }

        break;
      }
    }

    return menuItems;
  }

  confirmCancellation(application) {
    this.#withErrorHandler(this.args.confirmCancellation.perform(application));
  }

  reviewAdvance(application) {
    this.#withErrorHandler(this.args.reviewAdvance.perform(application));
  }

  declineAdvance(application) {
    showModal(this, 'confirmation', {
      titleText: 'Decline advance',
      bodyText: 'Are you sure you want to decline the advance request?',
      actionButtonText: 'Decline advance',
      isShowCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmAction: () => this.#withErrorHandler(this.args.cancelAdvance.perform(application)),
    });
  }

  cancelAdvance(application) {
    showModal(this, 'confirmation', {
      titleText: 'Cancel advance',
      bodyText: 'Are you sure you want to cancel your advance request?',
      actionButtonText: 'Cancel advance',
      isShowCancelButton: true,
      cancelButtonText: 'Do not cancel',
      confirmAction: () => this.#withErrorHandler(this.args.cancelAdvance.perform(application)),
    });
  }

  markComplete(application) {
    this.agreeToLegal(application, () => this.#withErrorHandler(this.args.markComplete.perform(application)));
  }

  requestAdvance(application) {
    this.args.requestAdvance.perform(application);
  }

  withdrawApplication(application) {
    showModal(this, 'lsg/lsg-modal-text-editor', {
      title: 'Withdraw your application',
      textAreaPlaceholder: 'Optional: Leave a note',
      cancelText: 'Cancel',
      confirmText: 'Withdraw',
      onConfirmMessage: (message) => this.#withErrorHandler(this.args.declineApplication.perform(application, message)),
    });
  }

  declineApplication(application) {
    showModal(this, 'lsg/lsg-modal-text-editor', {
      title: 'Decline this project',
      textAreaPlaceholder: 'Optional: Leave a note',
      cancelText: 'Cancel',
      confirmText: 'Decline',
      onConfirmMessage: (message) => this.#withErrorHandler(this.args.declineApplication.perform(application, message)),
    });
  }

  addOrEditLinkToProjectDigest(projectDigest) {
    showModal(this, 'work-samples/project', {
      projectDigest,
      isAddLinkToPublishedProject: !projectDigest?.id,
    });
  }

  acceptOffer(application) {
    this.agreeToLegal(application, () => {
      // accepting a legal agreement can change the state of the application behind the scenes.
      // if the status is `active` at this point, just call the task
      if (application.status === APPLICATION_STATUS.ACTIVE) {
        this.#withErrorHandler(this.args.acceptOffer.perform(application));
        return;
      }

      showModal(this, 'posting/make-offer-modal', {
        application,
        offerRecipientName: application.buyer.name,
        initialPayoutAmount: application.price,
        isApproveOffer: true,
        isFromSeller: true,
        onPriceConfirmed: () => this.#withErrorHandler(this.args.acceptOffer.perform(application)),
      });
    });
  }

  counterOffer(application) {
    showModal(this, 'posting/make-offer-modal', {
      application,
      offerRecipientName: application.buyer.name,
      initialPayoutAmount: application.price,
      isFromSeller: true,
      makingCounterOffer: true,
      onPriceConfirmed: (price) => this.#withErrorHandler(this.args.counterOffer.perform(application, price)),
    });
  }

  agreeToLegal(application, f) {
    const legalToShow = getRequiredLegal(application);

    if (!legalToShow) {
      return f();
    }

    showModal(this, 'projects/accept-terms-modal', {
      application,
      legalToShow,
      onLegalAgreed: (legal) => {
        this.args.agreeToLegal
          .perform(application, legal)
          .then(() => f())
          .catch((e) => handleErrorResponseWithModal(this, e));
      },
    });
  }

  #withErrorHandler(p) {
    p.catch((e) => handleErrorResponseWithModal(this, e));
  }
}

export default SellerProjectsResultItemComponent;
